import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Link,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { resetMasterPass, resetPasswordLink } from "../_redux/authCrud";
import { Link as RouterLink } from "react-router-dom";
import i18n from "./i18n";
import mainLogo from "../../../../../src/_metronic/layout/components/header/header-menu/logo.png";
import { getAuthErrorMessage } from "./utils/parseAuthApiError";

const NewForgetPass = (props) => {
  const { t } = useTranslation();
  const masterAdminCheck =
    window.location.pathname.split("/")[1] === "masterAdmin";
  const emailId = props.location?.state?.email || "";

  // form state
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    i18n.changeLanguage(props.selectedLanguage);
  }, [props.selectedLanguage]);

  // validation schemas
  const emailSchema = Yup.object().shape({
    email: Yup.string()
      .required(t("Password is required"))
      .email(t("Enter correct mail Id")),
  });
  const passwordSchema = Yup.object().shape({
    password: Yup.string().required(t("Password is required")),
  });

  // formik for email link
  const formikEmail = useFormik({
    initialValues: { email: emailId },
    validationSchema: emailSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setInvalid(false);
      setInvalidMessage("");
      try {
        await resetPasswordLink(
          values.email,
          window.location.origin + "/auth/reset"
        );
        setShowSuccess(true);
      } catch (e) {
        setInvalidMessage(
          getAuthErrorMessage(e, {
            fallback: t("Failed to send password reset link."),
          })
        );
        setInvalid(true);
      } finally {
        setLoading(false);
      }
    },
  });

  // formik for master password reset
  const formikPass = useFormik({
    initialValues: { password: "" },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setInvalid(false);
      setInvalidMessage("");
      try {
        const token = localStorage.getItem("token");
        await resetMasterPass(token, values.password);
        setShowSuccess(true);
      } catch (e) {
        setInvalidMessage(
          getAuthErrorMessage(e, {
            fallback: t("Failed to reset password."),
          })
        );
        setInvalid(true);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box display="flex" width="100vw" height="100vh">
      {/* LEFT PANEL */}
      <Box
        flex={1}
        bgcolor="#121212"
        color="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px={4}
      >
        <img
          alt="logo"
          // src={imageData?.logoUrl}
          src={mainLogo}
          style={{ maxHeight: "100px", maxWidth: "150px", filter: "invert(1)" }}
        />
        <Typography
          variant="h6"
          mb={4}
          style={{ fontWeight: "bold", fontSize: "1.8rem" }}
        >
          {t("Truly all-in-one platform for hotels and vacation rentals")}
        </Typography>
        <Typography
          variant="h5"
          color="white"
          mb={2}
          style={{ marginTop: "10px", marginBottom: "6rem" }}
        >
          {t("empowering 3500+ hotels worldwide")}
        </Typography>
        <Typography variant="h5" color="gray" mb={2}>
          {t("Download our app")}
        </Typography>
        <Box display="flex" gap={2}>
          <div className="d-flex justify-content-between ">
            <img
              style={{
                height: "4rem",
                cursor: "pointer",
                width: "11rem",
                marginTop: "7px",
                borderRadius: "10px",
                border: "0.8px solid #A3A3A3",
              }}
              onClick={() => {
                window.open(
                  "https://apps.apple.com/us/app/stayflexi-orchestra/id1498345123",
                  "_blank"
                );
              }}
              src={
                "https://prodimagesbucket.blob.core.windows.net/misc/GPLAYS.png"
              }
            />
            <img
              style={{
                height: "73px",
                width: "12rem",
                cursor: "pointer",
                marginTop: "-5px",
              }}
              onClick={() => {
                window.open(
                  "https://play.google.com/store/apps/details?id=com.stayflexi.platform",
                  "_blank"
                );
              }}
              src={
                "https://prodimagesbucket.blob.core.windows.net/misc/5a902dbf7f96951c82922875.png"
              }
            />
          </div>
        </Box>
      </Box>

      {/* RIGHT PANEL */}
      <Box flex={1} display="flex" flexDirection="column" px={6}>
        {/* CENTERED FORM */}
        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {!showSuccess ? (
            <Box
              component="form"
              noValidate
              onSubmit={
                masterAdminCheck
                  ? formikPass.handleSubmit
                  : formikEmail.handleSubmit
              }
              sx={{ width: "100%", maxWidth: 400 }}
            >
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Typography style={{ fontSize: "2rem" }} pb={1}>
                    Forgot your password?
                  </Typography>
                  <Typography variant="body1" mb={2}>
                    We will send you a link to create new password
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {!masterAdminCheck ? (
                    <TextField
                      fullWidth
                      autoFocus
                      label={t("Email")}
                      name="email"
                      id="email"
                      value={formikEmail.values.email}
                      onChange={(e) => {
                        formikEmail.setFieldValue("email", e.target.value);
                        if (invalid) setInvalid(false);
                        if (invalidMessage) setInvalidMessage("");
                      }}
                      onBlur={formikEmail.handleBlur}
                      error={
                        (formikEmail.touched.email &&
                          Boolean(formikEmail.errors.email)) ||
                        invalid
                      }
                      helperText={
                        (formikEmail.touched.email &&
                          formikEmail.errors.email) ||
                        (invalid && invalidMessage)
                      }
                    />
                  ) : (
                    <TextField
                      fullWidth
                      autoFocus
                      type={showPassword ? "text" : "password"}
                      label={t("New Password")}
                      name="password"
                      id="password"
                      value={formikPass.values.password}
                      onChange={(e) => {
                        formikPass.setFieldValue("password", e.target.value);
                        if (invalid) setInvalid(false);
                        if (invalidMessage) setInvalidMessage("");
                      }}
                      onBlur={formikPass.handleBlur}
                      error={
                        (formikPass.touched.password &&
                          Boolean(formikPass.errors.password)) ||
                        invalid
                      }
                      helperText={
                        (formikPass.touched.password &&
                          formikPass.errors.password) ||
                        (invalid && invalidMessage)
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} container justifyContent="space-between">
                  <Link
                    component={RouterLink}
                    to="/auth/login"
                    underline="hover"
                  >
                    <Button variant="outlined">{t("Back")}</Button>
                  </Link>
                  <LoadingButton
                    type="submit"
                    loading={loading}
                    variant="contained"
                  >
                    {t("Submit")}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box textAlign="center">
              <Typography variant="h5" mb={2}>
                {t("Email sent")}
              </Typography>
              <Typography variant="body1" mb={4}>
                {t(
                  "If the email you entered is associated with the account you should receive a link to create your new password shortly."
                )}
              </Typography>
              <Button
                component={RouterLink}
                to="/auth/login"
                variant="outlined"
              >
                {t("Done")}
              </Button>
            </Box>
          )}
        </Box>

        {/* FOOTER */}
        <Typography align="center" p={2} sx={{ color: "#5C5C5C" }}>
          {`© ${new Date().getFullYear()} ${t(
            "Stayflexi, Inc. San Francisco, California"
          )}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default NewForgetPass;
